import { getApiClient } from '@src/services/api-client'
import { MutationFunction, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import { StatusCount } from '../../types'
import { CreditApplicationReservation, CreditApplicationReservationDto } from '../../types/CreditApplicationReservation'
import { CreditTaskDashboardEntry } from '../../types/CreditTaskDashboardEntry'

const BASE_URL = '/CreditTaskDashboardEntries'
const STATUS_COUNT_BASE_URL = 'CreditTaskDashboardEntries/StatusCount'
const TRUSTEE_NAMES_LIST = '/CreditTaskDashboardEntries/BankruptcyTrusteeNames'

export const creditTaskDashboardKeys = {
  all: ['creditTaskDashboardEntries'] as const,
  list: (filters: Record<string, string | number | string[] | number[]> | undefined) =>
    [{ ...creditTaskDashboardKeys.all, filters }] as const,
  statusCount: (filters: Record<string, string | number | string[] | number[]> | undefined) =>
    [{ ...creditTaskDashboardKeys.all, type: 'statusCount', filters }] as const,
  trusteeNames: () => [{ ...creditTaskDashboardKeys.all, type: 'trusteeNames' }] as const,
}

function serializeParameters(params: Record<string, string | number | string[] | number[]>): string {
  return Object.entries(params)
    .map(([key, value]) => {
      if (Array.isArray(value)) {
        return value.map((v) => `${encodeURIComponent(key)}=${encodeURIComponent(v)}`).join('&')
      }
      return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
    })
    .join('&')
}

export async function getCreditTaskDashboardEntriesByFilters(
  filters?: Record<string, string | number | string[] | number[]>,
): Promise<CreditTaskDashboardEntry[]> {
  const apiClient = getApiClient()
  const queryString = filters ? `?${serializeParameters(filters)}` : ''
  const response = await apiClient.get<CreditTaskDashboardEntry[]>(`${BASE_URL}${queryString}`)
  return response.data
}

export function useGetCreditTaskDashboardEntries(
  filters?: Record<string, string | number | string[] | number[]>,
  enabled = true,
): [CreditTaskDashboardEntry[], boolean] {
  const { data, isFetching } = useQuery({
    queryKey: creditTaskDashboardKeys.list(filters),
    queryFn: () => getCreditTaskDashboardEntriesByFilters(filters),
    enabled,
  })
  return [data ?? [], isFetching]
}

export async function getStatusCount(
  filters?: Record<string, string | number | string[] | number[]>,
): Promise<StatusCount> {
  const apiClient = getApiClient()
  const queryString = filters ? `?${serializeParameters(filters)}` : ''
  const response = await apiClient.get<StatusCount>(`${STATUS_COUNT_BASE_URL}${queryString}`)
  return response.data
}

export function useGetStatusCount(
  filters?: Record<string, string | number | string[] | number[]>,
  enabled = true,
): [StatusCount | null, boolean] {
  const { data, isFetching } = useQuery({
    queryKey: creditTaskDashboardKeys.statusCount(filters),
    queryFn: () => getStatusCount(filters),
    enabled,
  })
  return [data ?? null, isFetching]
}

export async function getTrusteeNames(): Promise<string[]> {
  const apiClient = getApiClient()
  const response = await apiClient.get<string[]>(TRUSTEE_NAMES_LIST)
  return response.data
}

export function useGetTrusteeNames(enabled = true): [string[], boolean] {
  const { data, isFetching } = useQuery({
    queryKey: creditTaskDashboardKeys.trusteeNames(),
    queryFn: getTrusteeNames,
    enabled,
  })
  return [data ?? [], isFetching]
}

export async function reserveCreditTask(data: CreditApplicationReservationDto): Promise<CreditApplicationReservation> {
  const apiClient = getApiClient()
  const response = await apiClient.post<CreditApplicationReservation>(
    `/Applications/${encodeURIComponent(data.financingProgramId)}/${encodeURIComponent(data.creditApplicationId)}/Reservation`,
    data,
  )
  return response.data
}

export function useReserveCreditTask(
  currentFilters?: Record<string, string | number | string[] | number[]>,
): [MutationFunction<CreditApplicationReservation, CreditApplicationReservationDto>, boolean] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = useMutation({
    mutationFn: reserveCreditTask,
    onSuccess: (reservation) => {
      if (currentFilters) {
        const queryKey = creditTaskDashboardKeys.list(currentFilters)
        const currentData = queryClient.getQueryData<CreditTaskDashboardEntry[]>(queryKey)
        if (currentData) {
          queryClient.setQueryData<CreditTaskDashboardEntry[]>(
            queryKey,
            currentData.map((entry) =>
              entry.id === reservation.creditApplicationId
                ? { ...entry, reservedByUserId: reservation.userId, reservedByUserName: reservation.userName }
                : entry,
            ),
          )
        }
      }
    },
  })

  return [mutateAsync, isPending]
}

export function useInvalidateCreditTaskDashboard() {
  const queryClient = useQueryClient()

  return useCallback(
    async (filters?: Record<string, string | number | string[] | number[]>) => {
      await queryClient.invalidateQueries({
        queryKey: creditTaskDashboardKeys.list(filters),
      })
    },
    [queryClient],
  )
}
